import {round as _round} from 'lodash';

import {PIXELS_TO_REM_RATIO} from '../../constants';
import {convertFontSizeOfNodeToPixels} from '../../utils/fontSize';
import {getFontSizeContextInPxForNode} from '../../utils/fontSize/getFontSizeContextInPxForNode';
import {PIXEL_TO_PERCENTAGE_CONVERSION_FUNCTIONS} from './conversionFunctions';
import {convertValueToPixels} from './convertValueToPixels';
import {findClosestParentWithFontSize, findNodeById, findParentOfNodeById} from '../../../AstSearchers/FindAst';
import {validateForUnits, roundNumber} from '../../../helpers';

import type {SizeUnit} from '../../Properties';
import type {AstView, CSSProperty} from '../../../types';
import {extractUnitAndValue} from '../extractUnitAndValue';

interface CreationArgs {
    view: AstView;
    previewWidth?: number;
    nodeId: string;
    assignNewValueToNode?: boolean;
}

export type UnitConverter = ReturnType<typeof createUnitConverter>;
export function createUnitConverter({view, previewWidth, nodeId, assignNewValueToNode}: CreationArgs) {
    function converter<T extends 'auto', R extends 'auto'>(args: {key: CSSProperty; toUnit: T}): R;
    function converter<T extends SizeUnit, R extends number>(args: {key: CSSProperty; toUnit: T}): R;
    function converter({key, toUnit}) {
        return convertCssUnit({
            key,
            toUnit,
            view,
            previewWidth,
            nodeId,
            assignNewValueToNode,
        });
    }

    return converter;
}

interface BaseArgs {
    key: CSSProperty;
    view: AstView;
    previewWidth?: number;
    nodeId: string;
    assignNewValueToNode?: boolean;
    valueToConvert?: string | 'number';
}

interface ConvertToUnitArgs extends BaseArgs {
    toUnit: SizeUnit;
}

interface ConvertToAutoArgsArgs extends BaseArgs {
    toUnit: 'auto';
}

export function convertCssUnit(args: ConvertToAutoArgsArgs): 'auto';
export function convertCssUnit(args: ConvertToUnitArgs): number;
export function convertCssUnit({
    key,
    toUnit,
    view,
    nodeId,
    previewWidth = view.tree.position.width,
    assignNewValueToNode = true,
    valueToConvert = '',
}) {
    const node = findNodeById(view.tree, nodeId);

    if (!node) {
        return validateForUnits(key, 0, toUnit);
    }
    const inputValue = valueToConvert || node.styles[key];

    const assignConvertedValue = <V extends number | 'auto'>(value: V): V => {
        if (assignNewValueToNode) {
            node.styles[key] = validateForUnits(key as string, value, toUnit);
        }
        return value;
    };

    if (toUnit === 'auto') {
        return assignConvertedValue('auto');
    }

    const parentNode = findParentOfNodeById(view.tree, nodeId);
    const closestParentWithFontSize = findClosestParentWithFontSize(view.tree, nodeId);

    const {width: parentWidth, height: parentHeight} = parentNode?.innerPosition || {
        width: view.tree.position.width,
        height: view.tree.position.height,
    };

    const {value, unit: currentUnit} = extractUnitAndValue(inputValue);

    const numericValue = typeof value === 'number' ? value : parseFloat(value);

    if (currentUnit === toUnit) {
        return assignConvertedValue(numericValue);
    }

    // If the property doesn't have a parseable value we can't change/adjust it
    if ((value !== 'auto' && Number.isNaN(numericValue)) || value === 0) {
        return assignConvertedValue(0);
    }

    const {parent: parentFontSizePx, self: fontSizePx} = getFontSizeContextInPxForNode({
        cssKey: key,
        view: view.tree,
        node,
        parentHeight,
        parentWidth,
        closestParentWithFontSize,
        previewWidth,
    });

    const isFontSize = key === 'fontSize';
    const valueInPx = isFontSize
        ? convertFontSizeOfNodeToPixels(view.tree, node)
        : convertValueToPixels({
              cssKey: key,
              value: numericValue,
              unit: currentUnit,
              fontSize: fontSizePx,
              node,
              parentFontSize: parentFontSizePx,
              parentHeight,
              parentWidth,
              previewWidth,
          });

    if (toUnit === 'px') {
        return assignConvertedValue(roundNumber(valueInPx));
    }
    if (toUnit === 'rem') {
        return assignConvertedValue(_round(valueInPx / PIXELS_TO_REM_RATIO, 2));
    }
    if (toUnit === 'em') {
        return assignConvertedValue(_round(valueInPx / (isFontSize ? parentFontSizePx : fontSizePx), 2));
    }
    if (toUnit === 'vw') {
        return assignConvertedValue(_round((valueInPx / previewWidth) * 100, 2));
    }
    if (toUnit === '%') {
        // Absolutely positioned nodes ignore padding, so we want to use the `node.position` not
        // `node.innerPosition`
        const pWidth = node.calculatedLayoutAbstraction?.absolutePosition
            ? parentNode?.position.width || parentWidth
            : parentWidth;
        const pHeight = node.calculatedLayoutAbstraction?.absolutePosition
            ? parentNode?.position.height || parentHeight
            : parentHeight;

        const result =
            PIXEL_TO_PERCENTAGE_CONVERSION_FUNCTIONS[key as string | number]?.({
                valueInPx,
                parentWidth: pWidth,
                parentHeight: pHeight,
                parentFontSizePx,
                styles: node.styles,
                node: node,
                fontSizePx,
            }) || valueInPx;

        return assignConvertedValue(_round(result, 2));
    }

    return assignConvertedValue(0);
}
